import { useFormikContext } from "formik";
import { useEffect, useState } from "react";
import { AGENT_SKILL_TITLE_MAX_CHARS } from "@common/constants/agentSkill";
import type { AgentSkillFlaggedFinding } from "@common/schemas/domains/agentSkills/agent-skill.schema";
import {
    AgentSkillCategory,
    AgentSkillKind,
    AgentSkillLoadMode,
} from "@common/types/agentSkill";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { Markdown } from "@design-system/Markdown/Markdown";
import { Tabs } from "@design-system/Tabs/Tabs";
import { Tag } from "@design-system/Tag/Tag";
import { VStack } from "@design-system/VStack/VStack";
import {
    AGENT_SKILL_CATEGORY_LABELS,
    AGENT_SKILL_LOAD_MODE_DESCRIPTIONS,
    AGENT_SKILL_LOAD_MODE_LABELS,
    FIRM_SCOPE_LABEL,
    FLAGGED_FINDING_LABELS,
    FLAGGED_SAVE_MESSAGE,
    TOOL_NOT_ATTACHED_HINT,
} from "./agentSkillCopy";
import {
    AgentSkillFormValues,
    agentSkillBodyMaxChars,
    agentSkillDescriptionMaxChars,
    insertSectionSnippet,
} from "./agentSkillForm";
import styles from "./AgentSkills.module.scss";

const fields = createFormikFields<AgentSkillFormValues>();

const CATEGORY_OPTIONS = Object.values(AgentSkillCategory).map((value) => ({
    value,
    label: AGENT_SKILL_CATEGORY_LABELS[value],
}));

const LOAD_MODE_OPTIONS = Object.values(AgentSkillLoadMode).map((value) => ({
    value,
    label: AGENT_SKILL_LOAD_MODE_LABELS[value],
    description: AGENT_SKILL_LOAD_MODE_DESCRIPTIONS[value],
}));

interface Props {
    clientName: string | null;
    kind: AgentSkillKind;
    /** The tools a Kick skill attaches; the text may name them freely. */
    tools: string[];
    findings: AgentSkillFlaggedFinding[];
    warnings: string[];
}

const toolNotAttached = (warning: string): string | null =>
    warning.startsWith("tool_not_attached:")
        ? warning.slice("tool_not_attached:".length)
        : null;

export const AgentSkillFormFields = ({
    clientName,
    kind,
    tools,
    findings,
    warnings,
}: Props) => {
    const formik = useFormikContext<AgentSkillFormValues>();
    const [tab, setTab] = useState<"write" | "preview">("write");
    const isKick = kind === AgentSkillKind.KICK;
    const bodyMaxChars = agentSkillBodyMaxChars(kind);

    const hasBodyError = formik.submitCount > 0 && Boolean(formik.errors.body);
    useEffect(() => {
        if (hasBodyError || findings.length > 0) {
            setTab("write");
        }
    }, [hasBodyError, formik.submitCount, findings.length]);

    const bodyLength = formik.values.body.length;
    const unattachedTools = warnings.flatMap((warning) => {
        const tool = toolNotAttached(warning);
        return tool === null ? [] : [tool];
    });

    return (
        <VStack gap={16}>
            {!isKick && (
                <HStack gap={8} align="center">
                    <span className="text-secondary">Scope</span>
                    <Tag color="neutral" size="sm" outline>
                        {clientName ?? FIRM_SCOPE_LABEL}
                    </Tag>
                </HStack>
            )}
            <fields.Text
                name="title"
                label="Title"
                size="lg"
                maxLength={AGENT_SKILL_TITLE_MAX_CHARS}
                autoFocus
            />
            <fields.TextArea
                name="description"
                label="Description"
                size="lg"
                autoGrow
                minRows={2}
                maxLength={agentSkillDescriptionMaxChars(kind)}
            />
            {!isKick && (
                <HStack gap={16} align="start">
                    <fields.Select
                        name="category"
                        label="Category"
                        options={CATEGORY_OPTIONS}
                    />
                    <fields.Select
                        name="loadMode"
                        label="Frequency"
                        options={LOAD_MODE_OPTIONS}
                    />
                </HStack>
            )}
            <Tabs.Root
                value={tab}
                onValueChange={(value) =>
                    setTab(value === "preview" ? "preview" : "write")
                }
            >
                <Tabs.List>
                    <Tabs.Tab value="write">Write</Tabs.Tab>
                    <Tabs.Tab value="preview">Preview</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="write">
                    <VStack gap={8}>
                        <fields.TextArea
                            name="body"
                            label="  "
                            description={`${bodyLength.toLocaleString("en-US")} / ${bodyMaxChars.toLocaleString("en-US")} characters. Markdown.`}
                            size="lg"
                            autoGrow
                            minRows={12}
                            maxLength={bodyMaxChars}
                        />
                        {findings.length > 0 && (
                            <VStack gap={4} className="text-danger">
                                <strong>{FLAGGED_SAVE_MESSAGE}</strong>
                                {findings.map((finding, index) => (
                                    <span key={`${finding.code}-${index}`}>
                                        {FLAGGED_FINDING_LABELS[finding.code] ??
                                            finding.code}
                                        {finding.line !== undefined
                                            ? ` (line ${finding.line})`
                                            : ""}
                                        : “{finding.excerpt}”
                                    </span>
                                ))}
                            </VStack>
                        )}
                        {unattachedTools.length > 0 && (
                            <span className="text-secondary">
                                {TOOL_NOT_ATTACHED_HINT}:{" "}
                                {unattachedTools.join(", ")}
                            </span>
                        )}
                        <HStack justify="end">
                            <Button
                                type="button"
                                variant="tertiary"
                                size="sm"
                                onClick={() =>
                                    void formik.setFieldValue(
                                        "body",
                                        insertSectionSnippet(
                                            formik.values.body,
                                        ),
                                    )
                                }
                            >
                                Add section
                            </Button>
                        </HStack>
                    </VStack>
                </Tabs.Panel>
                <Tabs.Panel value="preview">
                    <div className={styles.preview}>
                        {formik.values.body.trim() === "" ? (
                            <span className="text-secondary">
                                Nothing to preview yet
                            </span>
                        ) : (
                            <Markdown>{formik.values.body}</Markdown>
                        )}
                    </div>
                </Tabs.Panel>
            </Tabs.Root>
            {isKick && tools.length > 0 && (
                <VStack gap={4}>
                    <span className="text-secondary">
                        Tools this skill attaches; the text may name them.
                    </span>
                    <HStack gap={4} wrap>
                        {tools.map((tool) => (
                            <Tag key={tool} color="neutral" size="sm" outline>
                                {tool}
                            </Tag>
                        ))}
                    </HStack>
                </VStack>
            )}
        </VStack>
    );
};
